import { ImageGrid } from '@/components/image-library/image-grid';
import { ImageSortDropdown } from '@/components/image-library/image-sort-dropdown';
import { ImageSubjectFilter } from '@/components/image-library/image-subject-filter';
import type { ImageLibraryListingState } from '@/components/image-library/use-image-library-listing';
import { useInfiniteScrollSentinel } from '@/components/image-library/use-infinite-scroll-sentinel';
import { UploadImageIcon } from '@/components/tiptap-editor/tiptap-icons/upload-image-icon';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { MaImage } from '@/lib/zybooks-api';
import { CircleX, ImageOff, Loader2 } from 'lucide-react';
import { useRef } from 'react';
import { getMaImageUrl } from './image-library-utils';

export interface ImageLibraryBrowseShellProps {
    listing: Pick<
        ImageLibraryListingState,
        | 'sortBy'
        | 'setSortBy'
        | 'isDescending'
        | 'setIsDescending'
        | 'subjectOptions'
        | 'selectedSubjects'
        | 'setSelectedSubjects'
        | 'disabledSubjects'
        | 'toggleSubjectEnabled'
        | 'onlyUnused'
        | 'setOnlyUnused'
        | 'onlyMyUploads'
        | 'setOnlyMyUploads'
        | 'loadingImages'
        | 'loadingMore'
        | 'showError'
        | 'displayedImages'
        | 'hasMore'
        | 'loadMore'
        | 'totalCount'
    >;
    imageGridProps: {
        onEdit?: (image: MaImage) => void;
        onEditAltText?: (image: MaImage) => void;
        onDelete?: (image: MaImage) => void;
        onPreview?: (image: MaImage) => void;
        onPick?: (image: MaImage) => void;
        pickerSelected?: MaImage | null;
    };
    uploadImageCallback?: () => void;
}

export function ImageLibraryBrowseShell({ listing, imageGridProps, uploadImageCallback }: ImageLibraryBrowseShellProps) {
    const {
        sortBy,
        setSortBy,
        isDescending,
        setIsDescending,
        subjectOptions,
        selectedSubjects,
        setSelectedSubjects,
        disabledSubjects,
        toggleSubjectEnabled,
        onlyUnused,
        setOnlyUnused,
        onlyMyUploads,
        setOnlyMyUploads,
        loadingImages,
        loadingMore,
        showError,
        displayedImages,
        hasMore,
        loadMore,
        totalCount,
    } = listing;
    const { pickerSelected, onPick } = imageGridProps;

    const imageGridScrollRef = useRef<HTMLDivElement>(null);

    const showImageGrid = !showError && !loadingImages && displayedImages.length > 0;

    useInfiniteScrollSentinel({
        scrollRootRef: imageGridScrollRef,
        enabled: showImageGrid,
        hasMore,
        isLoading: loadingImages,
        isLoadingMore: loadingMore,
        onLoadMore: loadMore,
    });

    return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="flex w-full min-w-0 shrink-0 flex-nowrap items-center gap-2 mb-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <label
                        htmlFor="subjects-filter"
                        className="shrink-0 text-sm whitespace-nowrap"
                    >
                        Subjects:
                    </label>
                    <ImageSubjectFilter
                        id="subjects-filter"
                        className="min-h-9 min-w-0 flex-1"
                        subjectOptions={subjectOptions}
                        selectedSubjects={selectedSubjects}
                        onSelectedSubjectsChange={setSelectedSubjects}
                        disabledSubjects={disabledSubjects}
                        onToggleSubjectEnabled={toggleSubjectEnabled}
                        disabled={onlyUnused}
                    />
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <label
                        htmlFor="unused-images-filter"
                        className="text-sm whitespace-nowrap"
                    >
                        Unused:
                    </label>
                    <Checkbox
                        id="unused-images-filter"
                        checked={onlyUnused}
                        onCheckedChange={checked => setOnlyUnused(checked === true)}
                    />
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <label
                        htmlFor="my-uploads-filter"
                        className="text-sm whitespace-nowrap"
                    >
                        My uploads:
                    </label>
                    <Checkbox
                        id="my-uploads-filter"
                        checked={onlyMyUploads}
                        onCheckedChange={checked => setOnlyMyUploads(checked === true)}
                    />
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <ImageSortDropdown
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        isDescending={isDescending}
                        setIsDescending={setIsDescending}
                    />
                    {uploadImageCallback && (
                        <Button
                            size="sm"
                            onClick={uploadImageCallback}
                        >
                            <UploadImageIcon className="mr-2 h-4 w-4" />
                            Upload Image
                        </Button>
                    )}
                </div>
            </div>

            {totalCount > 0 && !loadingImages && !showError && (
                <div className="mb-2 shrink-0 text-sm text-muted-foreground">
                    Showing {displayedImages.length} of {totalCount}
                </div>
            )}

            {onPick && (
                <div className={cn('mb-2 flex min-h-20 items-center', pickerSelected ? 'gap-3' : 'gap-1')}>
                    <span className="shrink-0 text-sm text-muted-foreground">Selected image:</span>
                    <div
                        className="relative size-20 shrink-0 overflow-hidden"
                        aria-hidden={!pickerSelected}
                    >
                        {pickerSelected ? (
                            <img
                                className="size-full object-cover"
                                src={getMaImageUrl(pickerSelected)}
                                alt={pickerSelected?.alt_text ?? ''}
                            />
                        ) : (
                            <div className="size-full flex items-center justify-start text-sm text-muted-foreground">None</div>
                        )}
                    </div>
                </div>
            )}

            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden pb-1">
                <div
                    ref={imageGridScrollRef}
                    className="flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300"
                >
                    {showError ? (
                        <div className="flex flex-1 flex-col items-center justify-center gap-2 py-8 text-center">
                            <CircleX className="mb-4 h-10 w-10 text-red-500" />
                            <div className="text-red-500">Error loading images. Please try again later.</div>
                        </div>
                    ) : loadingImages ? (
                        <div className="flex flex-1 flex-col items-center justify-center py-8 text-center text-gray-500">
                            <Loader2 className="mb-4 h-10 w-10 animate-spin" />
                            <div>Loading images...</div>
                        </div>
                    ) : displayedImages.length === 0 ? (
                        <div className="flex flex-1 flex-col items-center justify-center py-8 text-center text-gray-500">
                            <ImageOff className="mb-4 h-10 w-10" />
                            <div>No images found.</div>
                        </div>
                    ) : (
                        <>
                            <ImageGrid
                                scrollParentRef={imageGridScrollRef}
                                images={displayedImages}
                                {...imageGridProps}
                            />
                            {loadingMore && (
                                <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading more images...
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
